import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    AlertTriangle,
    ArrowRight,
    Camera,
    Check,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    Copy,
    ImageOff,
    Lock,
    RefreshCw,
    ShieldCheck,
    Terminal,
    Trash2,
    Upload,
    X,
} from "lucide-react";
import "./EnrollmentPhotoModal.css";
import {
    deleteEnrollmentPhoto,
    getEnrollmentPhotoErrorMessage,
    getEnrollmentPhotoMetadata,
    getEnrollmentRetryConflict,
    justQueuedEnrollment,
    replaceEnrollmentPhoto,
    retryEnrollment,
} from "../../api/enrollmentPhoto";
import type {
    EmployeeEnrollment,
    EnrollmentMatchedEmployee,
    EnrollmentPhotoMetadata,
} from "../../interfaces/SelfieEnrollment";
import { useBlobImageUrl } from "../../hooks/useBlobImageUrl";
import {
    enrollmentReason,
    enrollmentReasonKey,
    fmtDate,
    fmtWhen,
    fmtWhenLong,
    pctLabel,
} from "../../util/enrollmentReasons";
import { EnmSpin, EnrollmentChip } from "./EnrollmentChip";

/**
 * Enrollment Photo — the one modal (TAS-568), shared by the Employees roster (v2)
 * and the Selfie Exceptions page. Ported from the design bundle's enroll-modal.js.
 *
 * It fetches its own metadata (which carries `enrollment`), and owns upload,
 * retry and delete. Every successful change is reported through `onChanged` so
 * the host can patch its row and toast; failures go to `onError`.
 *
 * The legacy src/components/EnrollmentPhotoModal.tsx stays for EmployeeViewV1.
 */

export interface EnrollmentModalEmployee {
    employeeID: number;
    altEmployeeID: string;
    firstName: string;
    lastName: string;
}

export type EnrollmentChangeAction = "upload" | "replace" | "retry" | "delete";

export interface EnrollmentPhotoModalProps {
    employee: EnrollmentModalEmployee;
    /** The host row's last-known enrollment — used for first paint until the metadata returns. */
    enrollment?: EmployeeEnrollment | null;
    canEdit: boolean;
    onClose: () => void;
    onChanged: (enrollment: EmployeeEnrollment, action: EnrollmentChangeAction) => void;
    /** Duplicate state: "Open {first}'s photo ›". Omit to hide the link. */
    onOpenOther?: (matched: EnrollmentMatchedEmployee) => void;
    onError: (message: string) => void;
}

const MIN_BUSY_MS = 500;
const imagePath = (employeeID: number) => `/admin/employees/${employeeID}/enrollmentPhoto/image`;
const wait = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

/** One image, one blob URL — never shared across components. */
const Photo: React.FC<{ path: string | null; alt: string }> = ({ path, alt }) => {
    const { blobUrl, loading, error } = useBlobImageUrl(path);
    if (!path) return null;
    if (loading) return <EnmSpin lg />;
    if (error || !blobUrl) {
        return (
            <>
                <ImageOff />
                <span className="enm-empty-t">couldn’t load the photo</span>
            </>
        );
    }
    return <img src={blobUrl} alt={alt} />;
};

function fromMetadata(
    m: EnrollmentPhotoMetadata,
    hint: EmployeeEnrollment | null | undefined
): EmployeeEnrollment {
    if (m.enrollment) return m.enrollment;
    // Older API build: keep the host's hint when it agrees about photo presence.
    if (hint && (hint.state === "none") === !m.hasEnrollmentPhoto) return hint;
    return m.hasEnrollmentPhoto ? { state: "enrolled", enrolledAt: m.enrolledAt } : { state: "none" };
}

const EnrollmentPhotoModal: React.FC<EnrollmentPhotoModalProps> = ({
    employee,
    enrollment: hint,
    canEdit,
    onClose,
    onChanged,
    onOpenOther,
    onError,
}) => {
    const [enr, setEnr] = useState<EmployeeEnrollment | null>(hint ?? null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [busy, setBusy] = useState<null | "upload" | "retry" | "delete">(null);
    const [armed, setArmed] = useState(false);
    const [copied, setCopied] = useState(false);
    const [imageNonce, setImageNonce] = useState(0);
    const armTimer = useRef<number | null>(null);
    const copyTimer = useRef<number | null>(null);
    const mounted = useRef(true);
    const hintRef = useRef(hint);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            if (armTimer.current) window.clearTimeout(armTimer.current);
            if (copyTimer.current) window.clearTimeout(copyTimer.current);
        };
    }, []);

    // Load (and reload when the host switches employee, e.g. "Open Patrick's photo").
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError(null);
        setArmed(false);
        setEnr(hintRef.current ?? null);
        getEnrollmentPhotoMetadata(employee.employeeID)
            .then((m) => {
                if (!cancelled) setEnr(fromMetadata(m, hintRef.current));
            })
            .catch((err) => {
                if (!cancelled) setLoadError(getEnrollmentPhotoErrorMessage(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [employee.employeeID]);
    hintRef.current = hint;

    // Capture phase + stopPropagation: Escape closes only this modal (not the exception
    // detail underneath), and arrow keys don't walk the detail's queue behind it.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                if (!busy) onClose();
            } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                e.stopPropagation();
            }
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [onClose, busy]);

    const n: EmployeeEnrollment = enr ?? { state: "none" };
    const state = n.state;
    const has = state !== "none";
    const first = employee.firstName;
    const fullName = `${employee.firstName} ${employee.lastName}`;
    const reasonKey = enrollmentReasonKey(n.reason);
    const meta = enrollmentReason(n.reason);
    const matched = state === "failed" && reasonKey === "duplicate" ? n.matchedEmployee ?? null : null;
    const wide = !!matched;
    const attempts = n.attempts ?? 1;
    const photoPath =
        has && (!loading || hint) ? `${imagePath(employee.employeeID)}${imageNonce ? `?v=${imageNonce}` : ""}` : null;
    const ready = !(loading && !hint) && !loadError;

    const apply = useCallback(
        (next: EmployeeEnrollment, action: EnrollmentChangeAction) => {
            if (!mounted.current) return;
            setEnr(next);
            onChanged(next, action);
        },
        [onChanged]
    );

    const handleFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || busy) return;
        const action: EnrollmentChangeAction = has ? "replace" : "upload";
        setBusy("upload");
        try {
            const [resp] = await Promise.all([replaceEnrollmentPhoto(employee.employeeID, file), wait(MIN_BUSY_MS)]);
            setImageNonce((v) => v + 1);
            apply(resp.enrollment ?? justQueuedEnrollment(), action);
        } catch (err) {
            onError(`Couldn’t upload the photo for ${fullName}. ${getEnrollmentPhotoErrorMessage(err)}`);
        } finally {
            if (mounted.current) setBusy(null);
        }
    };

    const handleRetry = async () => {
        if (busy || state !== "failed") return;
        setBusy("retry");
        try {
            const [resp] = await Promise.all([retryEnrollment(employee.employeeID), wait(MIN_BUSY_MS)]);
            apply(resp.enrollment, "retry");
        } catch (err) {
            const conflict = getEnrollmentRetryConflict(err);
            if (conflict?.enrollment && mounted.current) setEnr(conflict.enrollment);
            onError(conflict?.message ?? `Couldn’t queue a retry for ${fullName}. ${getEnrollmentPhotoErrorMessage(err)}`);
        } finally {
            if (mounted.current) setBusy(null);
        }
    };

    const handleDelete = async () => {
        if (busy) return;
        if (!armed) {
            setArmed(true);
            if (armTimer.current) window.clearTimeout(armTimer.current);
            armTimer.current = window.setTimeout(() => setArmed(false), 3000);
            return;
        }
        if (armTimer.current) window.clearTimeout(armTimer.current);
        setBusy("delete");
        try {
            const resp = await deleteEnrollmentPhoto(employee.employeeID);
            apply(resp.enrollment ?? { state: "none" }, "delete");
            onClose();
        } catch (err) {
            if (mounted.current) {
                setBusy(null);
                setArmed(false);
            }
            onError(`Couldn’t delete the photo for ${fullName}. ${getEnrollmentPhotoErrorMessage(err)}`);
        }
    };

    const copyForSupport = async () => {
        const raw = n.state === "failed" ? n.reasonDetail : n.lastTransientError;
        const text =
            `Employee ${employee.altEmployeeID} · queue ${n.queueId ?? "—"} · ${state.toUpperCase()} · attempts ${attempts}\n` +
            (raw ?? "");
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            if (copyTimer.current) window.clearTimeout(copyTimer.current);
            copyTimer.current = window.setTimeout(() => setCopied(false), 1800);
        } catch {
            onError("Couldn’t copy to the clipboard. Select the text in the box instead.");
        }
    };

    /* ── pieces ─────────────────────────────────────────────────────────── */

    const overlay =
        state === "verifying" ? (
            <span className="enm-over s-verifying">
                <EnmSpin />
                Verifying
            </span>
        ) : state === "failed" ? (
            <span className="enm-over s-failed">
                <AlertTriangle />
                Rejected by Azure
            </span>
        ) : null;

    const photoArea = () => {
        if (loading && !hint) {
            return (
                <div className="enm-frame">
                    <EnmSpin lg />
                </div>
            );
        }
        if (!has) {
            return (
                <div className="enm-frame empty">
                    <Camera />
                    <span className="enm-empty-t">no photo on file</span>
                </div>
            );
        }
        if (matched) {
            const conf = n.confidence ?? null;
            return (
                <div className="enm-compare">
                    <div className="enm-pc">
                        <div className="enm-pc-lab">This upload</div>
                        <div className="enm-frame sm">
                            <Photo path={photoPath} alt={`Rejected enrollment photo for ${fullName}`} />
                            {overlay}
                        </div>
                        <div className="enm-pc-who">
                            <b>{fullName}</b>
                            <span className="enm-mono">{employee.altEmployeeID}</span>
                        </div>
                    </div>
                    <div className="enm-conf">
                        <div className="enm-conf-n">{pctLabel(conf)}</div>
                        <div className="enm-conf-l">match</div>
                    </div>
                    <div className="enm-pc">
                        <div className="enm-pc-lab">Already enrolled as</div>
                        <div className="enm-frame sm">
                            <Photo
                                path={matched.enrollmentPhotoUrl}
                                alt={`Enrollment photo for ${matched.firstName} ${matched.lastName}`}
                            />
                            {!matched.enrollmentPhotoUrl && (
                                <>
                                    <Camera />
                                    <span className="enm-empty-t">no photo on file</span>
                                </>
                            )}
                        </div>
                        <div className="enm-pc-who">
                            <b>
                                {matched.firstName} {matched.lastName}
                            </b>
                            <span className="enm-mono">{matched.altEmployeeID}</span>
                            <span className={`enm-act ${matched.active ? "on" : "off"}`}>
                                {matched.active ? "Active" : "Inactive"}
                            </span>
                        </div>
                        {onOpenOther && (
                            <button type="button" className="enm-link" onClick={() => onOpenOther(matched)}>
                                Open {matched.firstName.trim()}’s photo
                                <ChevronRight />
                            </button>
                        )}
                    </div>
                    {conf != null && (
                        <div className="enm-meter">
                            <div className="enm-bar">
                                <div className="enm-fill" style={{ width: `${Math.min(100, conf * 100)}%` }} />
                                <div className="enm-thr" style={{ left: "80%" }} />
                            </div>
                            <div className="enm-scale">
                                <span>0%</span>
                                <span>duplicate threshold 80%</span>
                                <span>100%</span>
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        return (
            <div className="enm-frame">
                <Photo
                    path={photoPath}
                    alt={state === "failed" ? `Rejected enrollment photo for ${fullName}` : `Enrollment photo for ${fullName}`}
                />
                {overlay}
            </div>
        );
    };

    const statusBlock = () => {
        if (state === "none") {
            return (
                <div className="enm-warn">
                    <AlertTriangle />
                    <div>
                        <b>{first}</b> can’t face-match clock in until a photo is uploaded. Photos can be captured in the
                        mobile app or uploaded here.
                    </div>
                </div>
            );
        }
        if (state === "enrolled") {
            const on = n.enrolledAt ? fmtDate(n.enrolledAt) : null;
            return (
                <div className="enm-line ok">
                    <CheckCircle />
                    <div>
                        {on ? (
                            <>
                                Uploaded <b>{on}</b> · verified for face-match clock-in on the mobile app.
                            </>
                        ) : (
                            <>Verified for face-match clock-in on the mobile app.</>
                        )}
                    </div>
                </div>
            );
        }
        if (state === "verifying") {
            return (
                <div className="enm-st s-verifying">
                    <div className="enm-st-top">
                        <EnmSpin lg />
                        <div>
                            <div className="enm-st-t">Checking this photo with Azure</div>
                            <div className="enm-st-s">
                                Uploaded {fmtWhen(n.queuedAt ?? n.enrolledAt)} · usually done within 15 minutes
                                {attempts > 1 && (
                                    <>
                                        {" "}
                                        · <b>attempt {attempts} of 3</b>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="enm-st-say">
                        {attempts > 1
                            ? "The first try hit a temporary error, so it’s being retried automatically. Nothing for you to do yet."
                            : "Nothing for you to do. This page shows the result once the check finishes; refresh to see it."}
                    </div>
                </div>
            );
        }
        const person = { firstName: first, lastName: employee.lastName };
        return (
            <div className={`enm-st s-failed ${meta.cls}`}>
                <div className="enm-st-top">
                    <EnrollmentChip enrollment={n} />
                    <span className="enm-fam">{meta.family}</span>
                </div>
                <div className="enm-st-say">{meta.say(person, n)}</div>
                <div className="enm-st-do">
                    <ArrowRight />
                    <div>{meta.doit(person)}</div>
                </div>
                <div className="enm-st-meta">
                    {n.requestedBy ? (
                        <>
                            Uploaded by <b>{n.requestedBy.name}</b>
                        </>
                    ) : (
                        <>Uploaded {fmtWhen(n.queuedAt ?? n.enrolledAt)}</>
                    )}{" "}
                    · failed {fmtWhen(n.failedAt)}
                    {attempts > 1 ? ` · after ${attempts} tries` : ""}
                </div>
            </div>
        );
    };

    const prevNote =
        n.previousEnrolledAt && (state === "failed" || state === "verifying") ? (
            <div className="enm-prev">
                <ShieldCheck />
                <div>
                    Clock-in still uses the <b>{fmtDate(n.previousEnrolledAt)}</b> photo until this one is{" "}
                    {state === "failed" ? "fixed" : "verified"}.
                </div>
            </div>
        ) : null;

    const tech =
        state === "failed" || state === "verifying" ? (
            <details className="enm-tech">
                <summary>
                    <Terminal />
                    Technical detail
                    <ChevronDown className="chev" />
                </summary>
                <div className="enm-tech-body">
                    <div className="enm-raw">
                        <div className="enm-tl">{state === "failed" ? "LastError" : "Last transient error"}</div>
                        <pre>
                            {state === "failed"
                                ? n.reasonDetail || "No error text was recorded."
                                : n.lastTransientError || "No error: waiting for the next worker run."}
                        </pre>
                        <button type="button" className="enm-copy" onClick={copyForSupport}>
                            {copied ? <Check /> : <Copy />}
                            {copied ? "Copied" : "Copy for support"}
                        </button>
                    </div>
                    <div className="enm-tgrid">
                        <TechCell label="Queue ID" value={n.queueId != null ? String(n.queueId) : "—"} />
                        <TechCell label="Status" value={state === "failed" ? "FAILED" : "PENDING"} />
                        <TechCell label="Attempts" value={`${attempts} of 3`} />
                        <TechCell label="Queued" value={fmtWhenLong(n.queuedAt)} />
                        {n.failedAt && <TechCell label="Failed" value={fmtWhenLong(n.failedAt)} />}
                        <TechCell
                            label="Requested by"
                            value={
                                n.requestedBy
                                    ? `${n.requestedBy.name} (${n.requestedBy.adminEmployeeID})`
                                    : "null (queued before uploads were attributed)"
                            }
                        />
                        {state === "failed" && <TechCell label="reason" value={n.reason ?? "system"} />}
                    </div>
                </div>
            </details>
        ) : null;

    const footer = () => {
        const close = (
            <button type="button" className="enm-btn" onClick={onClose} disabled={!!busy}>
                Close
            </button>
        );
        if (!canEdit || !ready) return <div className="enm-foot">{close}</div>;
        const retryPrimary = state === "failed" && meta.emphasis === "retry";
        return (
            <div className="enm-foot">
                {close}
                {has && (
                    <button
                        type="button"
                        className={`enm-btn danger del${armed ? " armed" : ""}`}
                        onClick={handleDelete}
                        disabled={!!busy}
                        title="Remove the enrollment photo. The employee will no longer face-match."
                    >
                        {busy === "delete" ? <EnmSpin light={armed} /> : <Trash2 />}
                        {armed ? "Confirm delete" : "Delete"}
                    </button>
                )}
                {(state === "failed" || state === "verifying") && (
                    <button
                        type="button"
                        className={`enm-btn${retryPrimary ? " primary" : ""}`}
                        onClick={handleRetry}
                        disabled={state === "verifying" || !!busy}
                        title={
                            state === "verifying"
                                ? "Already checking. Retry becomes available if this attempt fails."
                                : "Check the photo on file with Azure again"
                        }
                    >
                        {busy === "retry" ? <EnmSpin light={retryPrimary} /> : <RefreshCw />}
                        {busy === "retry" ? "Queuing…" : "Retry"}
                    </button>
                )}
                <label className={`enm-btn${retryPrimary ? "" : " primary"}${busy ? " disabled" : ""}`}>
                    <input type="file" accept=".jpg,.jpeg,image/jpeg" onChange={handleFile} disabled={!!busy} />
                    {busy === "upload" ? <EnmSpin light={!retryPrimary} /> : <Upload />}
                    {busy === "upload" ? "Uploading…" : has ? "Replace photo" : "Upload photo"}
                </label>
            </div>
        );
    };

    return (
        <div
            className="enm-bg"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !busy) onClose();
            }}
        >
            <div className={`enm-modal${wide ? " wide" : ""}`} role="dialog" aria-label="Enrollment photo">
                <div className="enm-head">
                    <div className="enm-mi">
                        <Camera />
                    </div>
                    <div>
                        <h3>Enrollment Photo</h3>
                        <div className="enm-ms">
                            {fullName} · <span className="enm-mono">{employee.altEmployeeID || employee.employeeID}</span>
                        </div>
                    </div>
                    <button type="button" className="enm-x" onClick={onClose} disabled={!!busy} aria-label="Close">
                        <X />
                    </button>
                </div>

                <div className="enm-body">
                    {loadError ? (
                        <div className="enm-warn err">
                            <AlertTriangle />
                            <div>
                                <b>Couldn’t load the enrollment photo.</b> {loadError}
                            </div>
                        </div>
                    ) : (
                        <>
                            {photoArea()}
                            {ready && statusBlock()}
                            {ready && prevNote}
                            {ready && !canEdit && (state === "failed" || state === "none") && (
                                <div className="enm-ro">
                                    <Lock />
                                    <div>
                                        View only. Retrying, replacing or deleting a photo needs the{" "}
                                        <b>Edit enrollment photos</b> permission.
                                    </div>
                                </div>
                            )}
                            {ready && tech}
                        </>
                    )}
                </div>

                {footer()}
            </div>
        </div>
    );
};

const TechCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div>
        <div className="enm-tl">{label}</div>
        <div className="enm-tv">{value}</div>
    </div>
);

export default EnrollmentPhotoModal;
